import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CircularProgress } from '@/components/ui/circular-progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Timer, Play, Square, Loader2, Clock } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { useLanguage } from '@/contexts/LanguageContext';
import { useFasting } from '@/hooks/use-fasting';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

interface IntermittentFastingWidgetProps {
  organizationId?: string;
}

export function IntermittentFastingWidget({ organizationId }: IntermittentFastingWidgetProps) {
  const { t, dir } = useLanguage();
  const {
    session,
    settings,
    elapsedHours,
    elapsedMinutes,
    elapsedSeconds,
    progress,
    isFasting,
    loading,
    lastBreakTime,
    eatingWindowStartTime,
    eatingWindowHours,
    eatingElapsedHours,
    eatingElapsedMinutes,
    eatingElapsedSeconds,
    eatingProgress,
    startFasting,
    stopFasting,
    updateSettings,
  } = useFasting();

  const [isProcessing, setIsProcessing] = useState(false);

  // Available fasting plans
  const fastingPlans = [
    { value: '12:12', hours: 12, label: '12:12' },
    { value: '14:10', hours: 14, label: '14:10' },
    { value: '16:8', hours: 16, label: '16:8' },
    { value: '18:6', hours: 18, label: '18:6' },
    { value: '20:4', hours: 20, label: '20:4' },
    { value: '22:2', hours: 22, label: '22:2' },
    { value: '24:0', hours: 24, label: '24:0 (OMAD)' },
  ];

  const currentPlan = settings?.defaultPlan || '16:8';
  const targetHours = session?.targetHours || parseFastingPlan(currentPlan);

  const handleStartStop = async () => {
    if (isProcessing) return;

    try {
      setIsProcessing(true);
      if (isFasting) {
        await stopFasting();
        toast.success(t('dashboard.customer.dashboard.intermittentFasting.stopped') || 'Fasting stopped');
      } else {
        await startFasting(targetHours);
        toast.success(t('dashboard.customer.dashboard.intermittentFasting.started') || 'Fasting started');
      }
    } catch (error: any) {
      console.error('Error toggling fasting:', error);
      toast.error(error.message || t('dashboard.customer.dashboard.intermittentFasting.error') || 'An error occurred');
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePlanChange = async (newPlan: string) => {
    if (isProcessing || isFasting) return; // Can't change plan during active fast

    try {
      setIsProcessing(true);
      await updateSettings({ defaultPlan: newPlan });
      toast.success(t('dashboard.customer.dashboard.intermittentFasting.planUpdated') || 'Fasting plan updated');
    } catch (error: any) {
      console.error('Error updating plan:', error);
      toast.error(error.message || t('dashboard.customer.dashboard.intermittentFasting.error') || 'An error occurred');
    } finally {
      setIsProcessing(false);
    }
  };

  // Format time display
  const formatTime = () => {
    if (!isFasting) {
      return '00:00:00';
    }
    return `${String(elapsedHours).padStart(2, '0')}:${String(elapsedMinutes).padStart(2, '0')}:${String(elapsedSeconds).padStart(2, '0')}`;
  };

  // Calculate remaining time
  const calculateRemainingTime = () => {
    if (!isFasting || !session || targetHours === 0) return null;
    
    const elapsedTotalHours = elapsedHours + elapsedMinutes / 60 + elapsedSeconds / 3600;
    const remainingHours = Math.max(0, targetHours - elapsedTotalHours);
    const remainingHoursInt = Math.floor(remainingHours);
    const remainingMinutes = Math.floor((remainingHours - remainingHoursInt) * 60);
    
    return `${remainingHoursInt}h ${remainingMinutes}m`;
  };

  const remainingTime = calculateRemainingTime();

  if (loading) {
    return (
      <Card className="overflow-hidden">
        <CardHeader className="border-b bg-muted/50">
          <CardTitle className="flex items-center gap-2">
            <Timer className="h-5 w-5 text-rose-500" />
            {t('dashboard.customer.dashboard.intermittentFasting.title')}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden">
      <CardHeader className="border-b bg-muted/50">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Timer className="h-5 w-5" style={{ color: '#FF6B6B' }} />
            {t('dashboard.customer.dashboard.intermittentFasting.title')}
          </CardTitle>
          {!isFasting && (
            <Select
              value={currentPlan}
              onValueChange={handlePlanChange}
              disabled={isProcessing || isFasting}
            >
              <SelectTrigger 
                className={cn(
                  "w-auto h-8 px-2 text-xs bg-white hover:bg-gray-100",
                  "min-w-[70px]"
                )}
              >
                <SelectValue>
                  {fastingPlans.find(p => p.value === currentPlan)?.label || currentPlan}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {fastingPlans.map((plan) => (
                  <SelectItem key={plan.value} value={plan.value}>
                    {plan.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {isFasting && (
            <div className="text-xs font-medium text-muted-foreground">
              {fastingPlans.find(p => p.hours === targetHours)?.label || `Fasting ${targetHours}:${24 - targetHours}`}
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="px-4 py-8">
        <div className="grid gap-4" style={{ gridTemplateColumns: '35% 65%' }}>
          {/* First Column: Timer */}
          <div className="flex flex-col items-center justify-center space-y-3">
            {/* Timer - Show fasting when fasting, eating window when in eating window */}
            {isFasting ? (
              <CircularProgress
                value={progress}
                size={140}
                strokeWidth={10}
                progressColor="#FF6B6B" // Pink for fasting
                backgroundColor="rgba(255, 107, 107, 0.2)" // Light pink background
                className="animate-pulse-slow"
              >
                <div className="flex flex-col items-center justify-center text-center space-y-1">
                  <div className="text-[10px] font-medium text-muted-foreground">
                    {t('dashboard.customer.dashboard.intermittentFasting.fastingTime') || 'Fasting Time'}
                  </div>
                  <div className="text-2xl font-bold tabular-nums">
                    {formatTime()}
                  </div>
                  {remainingTime && (
                    <div className="text-[10px] text-muted-foreground">
                      {remainingTime}
                    </div>
                  )}
                </div>
              </CircularProgress>
            ) : eatingWindowStartTime ? (
              <CircularProgress
                value={eatingProgress}
                size={140}
                strokeWidth={10}
                progressColor="#7dda55" // Green for eating window
                backgroundColor="rgba(125, 218, 85, 0.2)" // Light green background
                className="animate-pulse-slow"
              >
                <div className="flex flex-col items-center justify-center text-center space-y-1">
                  <div className="text-[10px] font-medium text-muted-foreground">
                    {t('dashboard.customer.dashboard.intermittentFasting.eatingWindow') || 'Eating Window'}
                  </div>
                  <div className="text-2xl font-bold tabular-nums">
                    {`${String(eatingElapsedHours).padStart(2, '0')}:${String(eatingElapsedMinutes).padStart(2, '0')}:${String(eatingElapsedSeconds).padStart(2, '0')}`}
                  </div>
                  {(() => {
                    const remainingHoursTotal = Math.max(0, eatingWindowHours - (eatingElapsedHours + eatingElapsedMinutes / 60 + eatingElapsedSeconds / 3600));
                    const remainingHoursInt = Math.floor(remainingHoursTotal);
                    const remainingMinsInt = Math.floor((remainingHoursTotal - remainingHoursInt) * 60);
                    return remainingHoursTotal > 0 ? (
                      <div className="text-[10px] text-muted-foreground">
                        {remainingHoursInt}h {remainingMinsInt}m {t('dashboard.customer.dashboard.intermittentFasting.remaining') || 'remaining'}
                      </div>
                    ) : (
                      <div className="text-[10px] text-muted-foreground">
                        {t('dashboard.customer.dashboard.intermittentFasting.windowEnded') || 'Window ended'}
                      </div>
                    );
                  })()}
                </div>
              </CircularProgress>
            ) : (
              <CircularProgress
                value={0}
                size={140}
                strokeWidth={10}
                progressColor="#7dda55"
                backgroundColor="rgba(125, 218, 85, 0.2)"
              >
                <div className="flex flex-col items-center justify-center text-center space-y-1">
                  <div className="text-2xl font-bold tabular-nums">
                    00:00:00
                  </div>
                  <div className="text-[10px] text-muted-foreground">
                    {t('dashboard.customer.dashboard.intermittentFasting.notStarted') || 'Not started'}
                  </div>
                </div>
              </CircularProgress>
            )}
          </div>

          {/* Second Column: Progress Bar, Explanation and Button */}
          <div className={cn(
            "flex flex-col justify-center space-y-3",
            dir === 'rtl' ? "pr-0 pl-12" : "pl-0 pr-12"
          )}>
            {/* Progress Bar */}
            {(isFasting || eatingWindowStartTime) && (
              <div className="w-full space-y-1">
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>
                    {isFasting 
                      ? `${elapsedHours}h ${elapsedMinutes}m`
                      : `${eatingElapsedHours}h ${eatingElapsedMinutes}m`
                    }
                  </span>
                  <span>
                    {isFasting ? `${targetHours}h` : `${eatingWindowHours}h`}
                  </span>
                </div>
                <div 
                  className={cn(
                    "h-1.5 rounded-full overflow-hidden bg-muted",
                    dir === 'rtl' && "[&>*]:transform [&>*]:scale-x-[-1]"
                  )}
                >
                  <motion.div
                    className="h-full rounded-full"
                    style={{ backgroundColor: isFasting ? '#FF6B6B' : '#7dda55' }}
                    initial={{ width: 0 }}
                    animate={{ width: `${isFasting ? progress : eatingProgress}%` }}
                    transition={{ duration: 0.5 }}
                  />
                </div>
              </div>
            )}

            {/* Explanatory Text */}
            <div className="space-y-2">
              {isFasting ? (
                <div className="text-xs text-muted-foreground">
                  {t('dashboard.customer.dashboard.intermittentFasting.fastingExplanation', {
                    target: targetHours.toString(),
                    eating: eatingWindowHours.toString()
                  }) || `You are currently fasting. Target: ${targetHours} hours. Your eating window of ${eatingWindowHours} hours will begin after you complete your fast.`}
                </div>
              ) : eatingWindowStartTime ? (
                <div className="text-xs text-muted-foreground">
                  {t('dashboard.customer.dashboard.intermittentFasting.eatingExplanation', {
                    eating: eatingWindowHours.toString(),
                    plan: currentPlan
                  }) || `You are in your eating window. You can eat for ${eatingWindowHours} hours. Start fasting again after this window ends to continue your ${currentPlan} schedule.`}
                </div>
              ) : (
                <div className="text-xs text-muted-foreground">
                  {t('dashboard.customer.dashboard.intermittentFasting.readyExplanation', {
                    plan: currentPlan,
                    fasting: targetHours.toString(),
                    eating: eatingWindowHours.toString()
                  }) || `Select your fasting plan (${currentPlan}) and start your fast. You'll fast for ${targetHours} hours, then have an ${eatingWindowHours}-hour eating window.`}
                </div>
              )}
            </div>

            {/* Start/Stop Button */}
            <Button
              onClick={handleStartStop}
              disabled={isProcessing}
              size="sm"
              className={cn(
                "w-full h-9 px-4 text-sm font-semibold rounded-full text-white",
                isFasting
                  ? "hover:bg-[#E65C5C]"
                  : "hover:bg-[#6BC44A]"
              )}
              style={{ 
                backgroundColor: isFasting ? '#FF6B6B' : '#7dda55'
              }}
            >
              {isProcessing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  {t('dashboard.customer.dashboard.intermittentFasting.processing') || 'Processing...'}
                </>
              ) : isFasting ? (
                <>
                  <Square className="h-4 w-4 mr-2" />
                  {t('dashboard.customer.dashboard.intermittentFasting.stop') || 'Stop Fasting'}
                </>
              ) : (
                <>
                  <Play className="h-4 w-4 mr-2" />
                  {t('dashboard.customer.dashboard.intermittentFasting.start') || 'Start Fasting'}
                </>
              )}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Parse fasting plan string to hours
 * '16:8' -> 16, '18:6' -> 18, etc.
 */
function parseFastingPlan(plan: string): number {
  const match = plan.match(/^(\d+):/);
  return match ? parseInt(match[1], 10) : 16;
}
